import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, ApiError } from "@/lib/query-client";
import { formatPhoneDisplay } from "@shared/utils/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Trash2, Pencil } from "lucide-react";

/**
 * Minimal editor for a contact's labeled emails/phones (issue #402 part 2).
 *
 * Lists the contact's contact_methods, and lets staff add, edit (value + label),
 * remove, and mark-primary entries. Phones are shown with #401's formatPhoneDisplay.
 * The server validates and dedupes; a 400 (bad value / over-long label) or 409
 * (duplicate / primary-choice-required) is surfaced inline rather than thrown away.
 */

interface ContactMethod {
  id: string;
  kind: "email" | "phone";
  label: string | null;
  valueRaw: string;
  isPrimary: boolean;
}

const KEY = (contactId: string) => ["/api/contacts", contactId, "methods"];

function display(m: ContactMethod): string {
  return m.kind === "phone" ? formatPhoneDisplay(m.valueRaw) : m.valueRaw;
}

export function ContactMethodsEditor({ contactId }: { contactId: string }) {
  const queryClient = useQueryClient();
  const { data: methods = [], isLoading } = useQuery<ContactMethod[]>({ queryKey: KEY(contactId) });
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: KEY(contactId) });
  const onError = (e: unknown) => setError(e instanceof ApiError ? e.message : "Something went wrong.");

  // Add form
  const [kind, setKind] = useState<"email" | "phone">("email");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");

  const addMut = useMutation({
    mutationFn: () => apiRequest("POST", `/api/contacts/${contactId}/methods`, { kind, value, label: label || null }),
    onSuccess: () => { setValue(""); setLabel(""); setError(null); invalidate(); },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/contacts/${contactId}/methods/${id}`),
    onSuccess: () => { setError(null); invalidate(); },
    onError,
  });
  const primaryMut = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/contacts/${contactId}/methods/${id}/primary`),
    onSuccess: () => { setError(null); invalidate(); },
    onError,
  });
  const editMut = useMutation({
    mutationFn: (vars: { id: string; value: string; label: string | null }) =>
      apiRequest("PATCH", `/api/contacts/${contactId}/methods/${vars.id}`, { value: vars.value, label: vars.label }),
    onSuccess: () => { setEditingId(null); setError(null); invalidate(); },
    onError,
  });

  const busy = addMut.isPending || deleteMut.isPending || primaryMut.isPending || editMut.isPending;

  return (
    <div className="space-y-3" data-testid="contact-methods-editor">
      <h3 className="text-sm font-semibold">Emails &amp; Phones</h3>

      {error && (
        <p role="alert" className="text-sm text-destructive" data-testid="contact-methods-error">{error}</p>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : methods.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="contact-methods-empty">
          No emails or phones yet.
        </p>
      ) : (
        <ul className="space-y-1">
          {methods.map((m) =>
            editingId === m.id ? (
              <EditRow
                key={m.id}
                method={m}
                disabled={busy}
                onCancel={() => { setEditingId(null); setError(null); }}
                onSave={(v, l) => editMut.mutate({ id: m.id, value: v, label: l })}
              />
            ) : (
              <li key={m.id} className="flex items-center gap-2 text-sm" data-testid={`contact-method-${m.id}`}>
                <span className="w-16 text-muted-foreground capitalize shrink-0">{m.label || m.kind}</span>
                <span className="flex-1">{display(m)}</span>
                {m.isPrimary ? (
                  <span className="text-xs font-medium text-primary inline-flex items-center gap-1" data-testid={`primary-badge-${m.id}`}>
                    <Star className="h-3 w-3 fill-current" /> Primary
                  </span>
                ) : (
                  <Button type="button" variant="ghost" size="sm" disabled={busy}
                    onClick={() => primaryMut.mutate(m.id)} data-testid={`make-primary-${m.id}`}>
                    Make primary
                  </Button>
                )}
                <Button type="button" variant="ghost" size="sm" disabled={busy}
                  onClick={() => { setEditingId(m.id); setError(null); }} aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" disabled={busy}
                  onClick={() => deleteMut.mutate(m.id)} aria-label="Remove" data-testid={`remove-method-${m.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ),
          )}
        </ul>
      )}

      {/* Add a new labeled entry */}
      <div className="flex items-end gap-2">
        <div className="w-24">
          <Select value={kind} onValueChange={(v) => setKind(v as "email" | "phone")}>
            <SelectTrigger aria-label="Kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          className="w-28"
          placeholder="Label"
          value={label}
          maxLength={50}
          aria-label="Label"
          onChange={(e) => setLabel(e.target.value)}
        />
        <Input
          className="flex-1"
          placeholder={kind === "email" ? "name@example.com" : "555-123-4567"}
          value={value}
          aria-label="Value"
          onChange={(e) => setValue(e.target.value)}
        />
        <Button
          type="button"
          disabled={busy || !value.trim()}
          onClick={() => addMut.mutate()}
          data-testid="add-contact-method"
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function EditRow({
  method,
  disabled,
  onCancel,
  onSave,
}: {
  method: ContactMethod;
  disabled: boolean;
  onCancel: () => void;
  onSave: (value: string, label: string | null) => void;
}) {
  const [value, setValue] = useState(method.valueRaw);
  const [label, setLabel] = useState(method.label ?? "");
  return (
    <li className="flex items-end gap-2 text-sm" data-testid={`edit-method-${method.id}`}>
      <Input className="w-28" value={label} maxLength={50} aria-label="Label" onChange={(e) => setLabel(e.target.value)} />
      <Input className="flex-1" value={value} aria-label="Value" onChange={(e) => setValue(e.target.value)} />
      <Button type="button" size="sm" disabled={disabled || !value.trim()}
        onClick={() => onSave(value, label || null)} data-testid={`save-method-${method.id}`}>
        Save
      </Button>
      <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={onCancel}>
        Cancel
      </Button>
    </li>
  );
}
